/**
 * HELP-VALUES.UTILS
 * Values of the account inside the text of a guide : the text written in Strapi has markers like {seuil-bas}, replaced by the
 * real thresholds of the account when the guide is displayed (a guide that gives the default values would be wrong for an
 * account that changed them)
 */

export interface HelpValues {
  lowCeil: number //Low threshold of the colors of the results (0 to 100)
  highCeil: number //High threshold of the colors of the results (0 to 100)
  gollacCeil: number //Threshold of exposure of the Gollac display (note out of 10)
  gapLow: number //Gap employees / managers under which it is negligible (points out of 10)
  gapHigh: number //Gap employees / managers above which it is critical (points out of 10)
  participationMin: number //Minimum of people to display a result
  groupsHtml: string //Groups of the repartition for the display of the survey (see buildRepartitionGroupsHtml)
}

//French notation of a number with one decimal at most : 5.4 gives "5,4", 7 gives "7"
export function formatHelpNumber(value: number): string {
  return String(Math.round(value * 10) / 10).replace(".", ",")
}

const escapeHtml = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")

//Notes of a group : "1 à 6", "7 et 8", "9"
export function formatNotesRange(min: number, max: number): string {
  if (min >= max) return String(max)
  return max - min === 1 ? `${min} et ${max}` : `${min} à ${max}`
}

//Table of the groups of the repartition (name, notes and color) : the labels can be written by the account, so they are escaped
export function buildRepartitionGroupsHtml(groups: { min: number; max: number; label: string; color: string }[]): string {
  const rows = groups
    .map((group) => {
      const color = /^#[0-9a-f]{3,8}$/i.test(group.color) ? group.color : "#8C8C8C"
      return (
        `<tr><td><span class="help-dot" style="background-color:${color}"></span><strong>${escapeHtml(group.label)}</strong></td>` +
        `<td>${formatNotesRange(group.min, group.max)}</td></tr>`
      )
    })
    .join("")
  return `<figure class="table"><table><thead><tr><th>Groupe</th><th>Notes</th></tr></thead><tbody>${rows}</tbody></table></figure>`
}

//Replace the markers by the values of the account
export function replaceHelpValueTokens(html: string, values: HelpValues): string {
  return html
    .replace(/<p>\s*\{groupes-repartition\}\s*<\/p>|\{groupes-repartition\}/g, values.groupsHtml)
    .replace(/\{seuil-bas\}/g, formatHelpNumber(values.lowCeil / 10))
    .replace(/\{seuil-haut\}/g, formatHelpNumber(values.highCeil / 10))
    .replace(/\{seuil-gollac\}/g, formatHelpNumber(values.gollacCeil))
    .replace(/\{ecart-bas\}/g, formatHelpNumber(values.gapLow))
    .replace(/\{ecart-haut\}/g, formatHelpNumber(values.gapHigh))
    .replace(/\{seuil-participation\}/g, String(values.participationMin))
}

//Colors of the answers of a scale (the palette the participants see) : the note of an answer and its color go together
const NOTE_COLORS: Record<string, string> = { "0": "#eca49a", "2.5": "#eccf9a", "5": "#b0eacf", "7.5": "#71deaa", "10": "#20CA7E" }

//[risque:forte] and [favorable:bon] become small colored tags : red for the side of the risk, green for the favorable side
const TAG_TEXT = /^[^\][<>&"]{1,24}$/
function formatTag(kind: "risk" | "ok", text: string): string {
  return `<span class="help-tag help-tag-${kind}">${escapeHtml(text)}</span>`
}

//Chips of the tags of the RPS questions, with the labels and colors of the questions page : [etiquette:stress] [etiquette:autonomie] [etiquette:soutien] [etiquette:reconnaissance]
const CHIP_LABELS: Record<string, string> = {
  stress: "Stress",
  autonomie: "Autonomie",
  soutien: "Soutien social",
  reconnaissance: "Reconnaissance"
}

//[note:2.5] becomes a colored pill with the note "2,5", [pastille:2.5] becomes the colored dot of the answer
export function replaceHelpNoteTokens(html: string): string {
  return html
    .replace(/\[note:(0|2\.5|5|7\.5|10)\]/g, (_, note: string) => `<span class="help-pill" style="background-color:${NOTE_COLORS[note]}">${formatHelpNumber(Number(note))}</span>`)
    .replace(/\[pastille:(0|2\.5|5|7\.5|10)\]/g, (_, note: string) => `<span class="help-dot" style="background-color:${NOTE_COLORS[note]}"></span>`)
    .replace(/\[etiquette:(stress|autonomie|soutien|reconnaissance)\]/g, (_, kind: string) => `<span class="help-chip help-chip-${kind}">${CHIP_LABELS[kind]}</span>`)
    .replace(/\[risque:([^\]]+)\]/g, (match, text: string) => (TAG_TEXT.test(text) ? formatTag("risk", text) : match))
    .replace(/\[favorable:([^\]]+)\]/g, (match, text: string) => (TAG_TEXT.test(text) ? formatTag("ok", text) : match))
}
